import React, { useEffect, useState } from 'react';
import { TokensVService as TokensV2Service } from '../../../services/V2/gozer';
import { CreditCardTypeEnum, TokenType, TokensVService, BillingAccountsVService } from '../../../services/V3/gozer';
import { checkExpirationDate, formatDateForOffset, handleErrorToast } from '../../shared/Functions';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link } from '@material-ui/core';
import { authProvider } from '../../../services/Security';
import { StyledCustomTable } from '../../shared/StyledCustomTable';
import StarIcon from '@material-ui/icons/Star';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import LoopIcon from '@material-ui/icons/Loop';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CCImage from './cc/CCImage';
import { ConfirmModal } from '../../shared/ConfirmModal';
import { WalletUpdate } from './WalletUpdate';
import { DistrictsVService, ModuleConfigsVService, PortalConfigsVService } from '../../../services/V2/padre';
import ThirdPartyACH from './ThirdPartyACH';

export const Wallet = ({ successToast, errorToast }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [refresh, setRefresh] = useState<boolean>(true);
    const [account, setAccount] = useState<any>()
    const [billingAccountId, setBillingAccountId] = useState<any>()
    const [token, setToken] = useState<any>()
    const [noMethodsModal, setNoMethodsModal] = useState<boolean>(false)
    const [tokens, setTokens] = useState<any[]>([])
    const [showDelete, setShowDelete] = useState<string | null>(null);
    const [showUpdate, setShowUpdate] = useState<string | null>(null);
    const [showAutopay, setShowAutopay] = useState<string | null>(null);
    const [showDisableAutopay, setShowDisableAutopay] = useState<string | null>(null);
    const [isPrimary, setIsPrimary] = useState<boolean>(false);
    const [isRecurring, setIsRecurring] = useState<boolean>(false);
    const [paymentmethod, setPaymentmethod] = useState<any>(null);
    const [districtModules, setDistrictModules] = useState<any>();
    const [districtId, setDistrictId] = useState<any>();
    const [isThirdPartyAchRecurring, setIsThirdPartyAchRecurring] = useState<boolean>(false)
    const [showThirdPartyCreate, setShowThirdPartyCreate] = useState<boolean>(false)
    const [offset, setOffset] = useState<number>(0)

    window.addEventListener('message', event => {
        // IMPORTANT: check the origin of the data!
        if (!event.data) return;
        var data = JSON.parse(event?.data);
        authProvider.setToken(data?.token)
        setBillingAccountId(data?.billingAccountId)
        setToken(data?.token)
    });

    useEffect(() => {
        if (!token || !billingAccountId || loading) return;
        setLoading(true)
        BillingAccountsVService.billingAccountsVGetById(billingAccountId ?? "")
            .then(x => {
                setAccount(x);
                setDistrictId(x.districtId);
                setIsThirdPartyAchRecurring(!(x.paymentInfo?.ach?.isI3Recurring ?? true))
            })
            .catch(x => {
                errorToast(handleErrorToast(x));
                setLoading(false);
            })
    }, [token, billingAccountId])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (account && districtModules?.ePayments) {
            getTokens()
        }
    }, [account, districtModules]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!districtId) return;
        setLoading(true)
        ModuleConfigsVService.moduleConfigsVGetDistrictModuleConfig(districtId)
            .then((x: any) => {
                setDistrictModules(x);
            })
            .catch(x => errorToast(handleErrorToast(x)))
        DistrictsVService.districtsVGetById(districtId)
            .then(district => {
                PortalConfigsVService.portalConfigsVGetAll(district?.operatorId ?? "")
                        .then((x: any) => {
                            try {
                                const nowDate = new Date();
                                const utcString = nowDate.toLocaleString('en-US', { timeZone: 'UTC' });
                                const tzString = nowDate.toLocaleString('en-US', { timeZone: x.timezone });
                                const utcDate = new Date(utcString);
                                const tzDate = new Date(tzString);
                                setOffset(Math.round((tzDate.getTime() - utcDate.getTime()) / 60000));
                            } catch (e) {
                            }
                        })
                        .catch((x) => {});
            })
            .catch((x) => {});
    }, [districtId]); // eslint-disable-line react-hooks/exhaustive-deps

    const getTokens = () => {
        setLoading(true)
        TokensV2Service.tokensVGetAll(account.id)
            .then((tokenList) => {
                setTokens(tokenList.filter(y => (y.tokenType === TokenType.ACH && !account.disallowACHPayments && account.paymentInfo.ach.allowPayment) ||
                    (
                        y.tokenType === TokenType.CC && !account.disallowCCPayments && account.paymentInfo.cc.allowPayment && (
                            (y.cardType === CreditCardTypeEnum.AMERICAN_EXPRESS && account.paymentInfo.cc.acceptAmex) ||
                            (y.cardType === CreditCardTypeEnum.DISCOVER && account.paymentInfo.cc.acceptDiscover) ||
                            (y.cardType === CreditCardTypeEnum.MASTER_CARD && account.paymentInfo.cc.acceptMasterCard) ||
                            (y.cardType === CreditCardTypeEnum.VISA && account.paymentInfo.cc.acceptVisa)
                        )
                    )))
                setLoading(false);
            })
            .catch((err) => {
                err.message !== 'Not Found' && errorToast(handleErrorToast(err));
                setLoading(false);
            });
    }

    const selectPaymentMethod = (item) => {
        setPaymentmethod(item)
    }

    const deleteItem = () => {
        setLoading(true)
        TokensVService
            .tokensVDeleteToken(account.id, showDelete ?? "")
            .then(result => {
                const successMessage = paymentmethod.tokenType === TokenType.CC
                    ? "Credit Card Deleted"
                    : "Bank Account Deleted";
                successToast(successMessage)
                getTokens()
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleErrorToast(err))
                getTokens()
            })
        setShowDelete(null)
    }

    const addAutopay = (enable) => {
        setLoading(true)
        TokensV2Service
            .tokensVUpdateRecurring(account.id, showAutopay ?? "", enable)
            .then(result => {
                getTokens()
                successToast("AutoPay Updated")
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleErrorToast(err))
                getTokens()
            })
        setShowAutopay(null)
    }

    const removeAutopay = (enable) => {
        setLoading(true)
        TokensV2Service
            .tokensVUpdateRecurring(account.id, showDisableAutopay ?? "", enable)
            .then(result => {
                getTokens()
                successToast("AutoPay Updated")
            }).catch(err => {
                err.message !== "Not Found" && errorToast(handleErrorToast(err))
                getTokens()
            })
        setShowDisableAutopay(null)
    }

    return loading ?
        <div className='loading-container'><CircularProgress color="primary" className='center-spinner' /></div>
        :
        districtModules && !districtModules.ePayments ?
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "590px", width: "1600px" }}>
                Access Denied.  Electronic payments have been disabled.
                <br />
                You may now close this window.
            </div>
            :
            <>
                {/* confirm modals */}
                <ConfirmModal
                    fullWidth={true}
                    cancelCallback={() => setShowAutopay(null)}
                    show={showAutopay !== null}
                    confirmCallback={() => addAutopay(true)}
                    title={"Enroll AutoPay"}
                    description={"Are you sure that you would like to enroll this payment method in AutoPay?"} />
                <ConfirmModal
                    fullWidth={false}
                    cancelCallback={() => setShowDisableAutopay(null)}
                    show={showDisableAutopay !== null}
                    confirmCallback={() => removeAutopay(false)}
                    title={"Unenroll AutoPay"}
                    description={tokens.find(x => x.id === showDisableAutopay)?.isThirdParty ?
                        `By submitting this form I understand that I am cancelling my automatic bank draft authorization.
                        This cancellation will take effect once processed, which may take up to 1 business day.
                        I acknowledge that payments already scheduled may still be withdrawn, and that I remain responsible for paying any outstanding balance by the due date.
                        If you have any questions or need help with your billing, feel free to contact ${account.districtInfo.name} - we are always here to help.` :
                        "Are you sure that you would like to disable AutoPay for this payment method?"} />
                <ConfirmModal
                    fullWidth={false}
                    cancelCallback={() => setShowDelete(null)}
                    show={showDelete !== null}
                    confirmCallback={deleteItem}
                    title={"Delete Payment Method"}
                    description={tokens.find(x => x.id === showDelete)?.isThirdParty ?
                        (`By submitting this form I understand that I am cancelling my automatic bank draft authorization.
                        This cancellation will take effect once processed, which may take up to 1 business day.
                        I acknowledge that payments already scheduled may still be withdrawn, and that I remain responsible for paying any outstanding balance by the due date.
                        If you have any questions or need help with your billing, feel free to contact ${account.districtInfo.name} - we are always here to help.`) :
                        (<><div>Are you sure that you would like to delete this payment method?</div>
                            {isPrimary ? <div>This payment method is currently set as your primary payment method.</div> : ""}
                            {isRecurring ? <div>This payment method is currently enrolled in autopay.</div> : ""} </>)} />
                {/* confirm modals end */}
                {showUpdate &&
                    <WalletUpdate
                        success={successToast}
                        fail={errorToast}
                        paymentMethod={showUpdate ?? ""}
                        account={account}
                        itemAdded={() => {
                            setShowUpdate(null)
                            getTokens();
                        }}
                        show={showUpdate !== null}
                        hide={() => setShowUpdate(null)} />
                }
                {showThirdPartyCreate &&
                    <ThirdPartyACH
                        success={() => {
                            successToast("Successfully added bank draft.")
                            getTokens()
                            setShowThirdPartyCreate(false)
                        }}
                        fail={errorToast}
                        account={account}
                        close={() => setShowThirdPartyCreate(false)}
                    />
                }
                {/* update modal end */}
                <Dialog onClose={() => { }} open={noMethodsModal}>
                    <DialogTitle>Account Notice</DialogTitle>
                    <DialogContent>
                        <p className="p-3 text-centered">
                            We’re sorry, payments are disabled for this account.
                        </p>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={() => setNoMethodsModal(false)}>Ok</Button>
                    </DialogActions>
                </Dialog>
                <Grid container style={{ backgroundColor: "#FFFFFF" }}>
                    {isThirdPartyAchRecurring && account.paymentInfo.ach.allowRecurringPayment &&
                        <Grid item className='clickable' xs={12} role="presentation">
                            <nav aria-label="Payment method options">
                                <ul style={{listStyle: "none", padding: 0, margin: 0, display: "flex", gap: "1em"}}>
                                    <li>
                                        <Link onClick={() => {
                                                setShowThirdPartyCreate(true)
                                            }}>
                                            Enroll Automated Bank Draft
                                        </Link>
                                    </li>
                                </ul>
                            </nav>
                        </Grid>
                    }

                    <Grid item xs={12}>
                        <Grid container style={{ padding: "20px 20px 50px 20px" }}>
                            <StyledCustomTable
                                title={''}
                                refresh={refresh}
                                setRefresh={setRefresh}
                                columns={[
                                    {
                                        field: "isPrimary",
                                        title: "Primary",
                                        editable: 'never',
                                        render: params =>
                                            <div style={{ color: params.isPrimary ? "#0073A8" : "gray" }}>
                                                <StarIcon style={{ fontSize: "25px" }} />
                                            </div>,
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "tokenType",
                                        title: "Payment Method",
                                        editable: 'never',
                                        render: params =>
                                            <div>
                                                {params.tokenType === TokenType.ACH ?
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <div style={{ marginRight: "5px" }}>
                                                            <AccountBalanceIcon />
                                                        </div>
                                                        <div style={{ marginLeft: "5px" }}>
                                                            ****{params.last4}
                                                        </div>
                                                        {params.isThirdParty &&
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                <div style={{ marginRight: "10px" }}>{params.bankName}</div>
                                                            </div>
                                                        }
                                                    </div>
                                                    :
                                                    <div>
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            <div style={{ marginRight: "5px" }}>
                                                                <CCImage token={params} className="cc-vendor-image-sm" />
                                                            </div>
                                                            <div style={{ marginLeft: "5px" }}>
                                                                ****{params.last4}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            <div style={{ marginRight: "10px" }}>{params.expirationMonth ?? "N"}/{params.expirationYear ?? "A"}</div>
                                                            {checkExpirationDate(params.expirationMonth, params.expirationYear) === "Expired" ?
                                                                <div style={{ color: "red", marginLeft: "10px" }}>Expired</div>
                                                                : checkExpirationDate(params.expirationMonth, params.expirationYear) === "Expiring" ?
                                                                    <div style={{ color: "red", marginLeft: "10px" }}>Expiring</div>
                                                                    : <></>
                                                            }
                                                        </div>
                                                    </div>
                                                }
                                            </div>,
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "processorId",
                                        title: "Processing Service",
                                        editable: 'never',
                                        render: params =>
                                                (<div>
                                                    {params.isThirdParty ? "3rd Party Bank" : "i3 Payment"}
                                                </div>),
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "isRecurring",
                                        title: "Manage Autopay",
                                        editable: 'never',
                                        render: params =>
                                                (<div>
                                                    {params.tokenType === TokenType.ACH && isThirdPartyAchRecurring && !params.isThirdParty ? "" :
                                                        ((params.tokenType === TokenType.ACH && account.paymentInfo.ach.allowRecurringPayment) ||
                                                            (params.tokenType === TokenType.CC && account.paymentInfo.cc.allowRecurringPayment &&
                                                                checkExpirationDate(params.expirationMonth, params.expirationYear) !== "Expired")) &&
                                                            <div className="clickable" onClick={() => { !params.isRecurring ? setShowAutopay(params.id) : setShowDisableAutopay(params.id) }} style={{ color: params.isRecurring ? "green" : "gray", alignItems: "center", display: "flex", flexDirection: "column" }}>
                                                                <LoopIcon style={{ fontSize: "25px" }} />
                                                                <div>
                                                                    {!params.isRecurring ?
                                                                        <div>Enroll Recurring Payment</div> :
                                                                        <div>Unenroll Recurring Payment</div>}
                                                                </div>
                                                            </div>
                                                    }
                                                </div>),
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "enrollmentDate",
                                        title: "",
                                        editable: 'never',
                                        render: params =>
                                                (<div>
                                                    {params.isRecurring && params.isThirdParty && offset &&
                                                        <div>
                                                            Enrolled: {formatDateForOffset(params.createdAt, offset)}
                                                        </div>
                                                    }
                                                </div>),
                                        hidden: false,
                                        sorting: false
                                    },
                                    {
                                        field: "",
                                        title: "",
                                        editable: 'never',
                                        render: params =>
                                            params.isThirdParty ?
                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <div className="clickable" onClick={() => {
                                                        selectPaymentMethod(params)
                                                        setIsPrimary(params.isPrimary)
                                                        setIsRecurring(params.isRecurring)
                                                        setShowDelete(params.id);
                                                    }} style={{ color: "black", width: "16%", marginRight: "10px" }}>
                                                        <DeleteIcon style={{ fontSize: "25px" }} />
                                                        <div>
                                                            Delete
                                                        </div>
                                                    </div>
                                                </div> :
                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <div className="clickable" onClick={() => setShowUpdate(params)}
                                                        style={{ color: "black", width: "16%", marginRight: "30px" }}>
                                                        <EditIcon style={{ fontSize: "25px" }} />
                                                        <div>Edit</div>
                                                    </div>
                                                    <div className="clickable" onClick={() => {
                                                        selectPaymentMethod(params)
                                                        setIsPrimary(params.isPrimary)
                                                        setIsRecurring(params.isRecurring)
                                                        setShowDelete(params.id);
                                                    }} style={{ color: "black", width: "16%", marginRight: "10px" }}>
                                                        <DeleteIcon style={{ fontSize: "25px" }} />
                                                        <div>
                                                            Delete
                                                        </div>
                                                    </div>
                                                </div>,
                                        hidden: false,
                                        sorting: false
                                    }
                                ]}
                                dataEndpoint={async query => {
                                    let data = tokens
                                    setRefresh(true)
                                    return {
                                        data: data ?? [],
                                        totalCount: data?.length ?? 0,
                                        numberPerPage: 10
                                    }
                                }}
                                actions={[]}
                                pageSize={10}
                                onRowClick={null}
                                tableHeight={"400px"}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                {loading &&
                    <div className='loading-container'><CircularProgress color="primary" className='center-spinner' /></div>
                }
            </>
}